import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDataSource } from "@/data/DataProvider";
import { CLIENT_ACCESS_LOADING, type ClientAccess, type ClientIdentity } from "@/data/types";
import { clearNext } from "./nextPath";

interface ClientAuthValue {
  access: ClientAccess;
  /** Non-null only when access.state === "ready". Convenience for hooks. */
  identity: ClientIdentity | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const ClientAuthContext = createContext<ClientAuthValue | null>(null);

const SIGNED_OUT: ClientAccess = { state: "unauthenticated", user: null, identity: null };

export function ClientAuthProvider({ children }: { children: ReactNode }) {
  const { auth } = useDataSource();
  // A device with no session at all goes straight to sign-in; the check below still runs and agrees.
  const [access, setAccess] = useState<ClientAccess>(() => (auth.knownSignedOut?.() ? SIGNED_OUT : CLIENT_ACCESS_LOADING));
  const queryClient = useQueryClient();

  const refresh = useCallback(async () => {
    setAccess(await auth.resolveAccess());
  }, [auth]);

  useEffect(() => {
    let cancelled = false;
    auth.resolveAccess().then((next) => {
      if (!cancelled) setAccess(next);
    });

    const unsubscribe = auth.onChange((event) => {
      if (event === "signed_out") {
        queryClient.clear();
        setAccess(SIGNED_OUT);
        return;
      }
      auth.resolveAccess().then((next) => {
        if (!cancelled) setAccess(next);
      });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [auth, queryClient]);

  const signOut = useCallback(async () => {
    await auth.signOut();
    queryClient.clear();
    setAccess(SIGNED_OUT);
    try {
      localStorage.removeItem("tfwp.viewMode");
      // The next person signed in on this device is asked about AI answers for themselves.
      localStorage.removeItem("tfwp.assistant.aiConsent");
    } catch {
      /* ignore */
    }
    clearNext();
  }, [auth, queryClient]);

  const value = useMemo<ClientAuthValue>(
    () => ({ access, identity: access.state === "ready" ? access.identity : null, refresh, signOut }),
    [access, refresh, signOut],
  );

  return <ClientAuthContext.Provider value={value}>{children}</ClientAuthContext.Provider>;
}

export function useClientAuth(): ClientAuthValue {
  const ctx = useContext(ClientAuthContext);
  if (!ctx) throw new Error("useClientAuth must be used inside ClientAuthProvider");
  return ctx;
}

/** For screens inside AuthGate: identity is guaranteed. */
export function useIdentity(): ClientIdentity {
  const { identity } = useClientAuth();
  if (!identity) throw new Error("useIdentity called outside an authenticated route");
  return identity;
}
